interface ArmorResponse {
	message?: string;
	detail?: string;
	outcome?: string;
	path?: string;
	waluWarning?: string;
}

class FFArmor {
	registrationId = '';

	DOM: {
		form: HTMLFormElement;
		submitButton: HTMLButtonElement;
		spinner: HTMLElement;
		allInputs: NodeListOf<HTMLInputElement | HTMLSelectElement>;
		email: HTMLInputElement;
		orderNumber: HTMLInputElement;
		serial: HTMLInputElement;
		sku: HTMLInputElement;
		serialCol: HTMLElement;
		dateCol: HTMLElement;
	} = {} as FFArmor['DOM'];

	state = {
		disabled: false,
		working: false,
		valid: false,
		error: null as string | null,
		success: null as boolean | null,
		emailIsValid: null as boolean | null,
		ambiguous: false,
	};

	constructor() {
		this.cache();
		if (!this.DOM.form) return;
		this.handleQueryParams();
		this.events();
		this.render();
	}

	cache() {
		const form = document.querySelector('.js-armorForm');
		if (!form) return;
		this.DOM = {
			form: form as HTMLFormElement,
			submitButton: form.querySelector('.js-submitButton') as HTMLButtonElement,
			spinner: form.querySelector('.js-spinner') as HTMLElement,
			allInputs: form.querySelectorAll('input, select'),
			email: form.querySelector('.js-customerEmail') as HTMLInputElement,
			orderNumber: form.querySelector('.js-orderNumber') as HTMLInputElement,
			serial: form.querySelector('.js-serial') as HTMLInputElement,
			sku: form.querySelector('.js-sku') as HTMLInputElement,
			serialCol: form.querySelector('.js-serialCol') as HTMLElement,
			dateCol: form.querySelector('.js-dateCol') as HTMLElement,
		};
	}

	events() {
		this.DOM.form.addEventListener('submit', this.formSubmit.bind(this));
		this.DOM.form.addEventListener('keyup', this.formKeyUp.bind(this));
		for (let i = 0; i < this.DOM.allInputs.length; i++) {
			this.DOM.allInputs[i].addEventListener('input', this.globalInput.bind(this));
		}
	}

	handleQueryParams() {
		const urlParams = new URLSearchParams(window.location.search);
		const order = urlParams.get('order');
		const id = urlParams.get('id');
		const model = urlParams.get('model');
		const ambiguous = urlParams.get('ambiguous');
		if (order) this.DOM.orderNumber.value = order;
		if (id) this.registrationId = id;
		if (model) this.DOM.sku.value = model;
		if (ambiguous) {
			this.state.ambiguous = true;
			this.DOM.serialCol.classList.remove('js-hide');
			this.DOM.dateCol.classList.remove('js-hide');
		}
		if (!id) {
			this.disableForm();
		}
	}

	formSubmit(e: SubmitEvent) {
		e.preventDefault();
		if (!this.validate() || this.state.working || this.state.disabled) return;
		this.state.working = true;
		this.render();
		const formData = new FormData(this.DOM.form);
		formData.append('action', 'armor_submit');
		formData.append('id', this.registrationId);

		grecaptcha.ready(() => {
			grecaptcha
				.execute(RECAPTCHA_SITE_KEY, { action: 'submit' })
				.then((token: string) => {
					formData.append('g-recaptcha-response', token);
					fetch(ajaxurl, {
						method: 'POST',
						body: formData,
					})
						.then((res) => this.formResponse(res))
						.catch((error) => this.handleSubmitFailure(error));
				})
				.catch((error) => this.handleSubmitFailure(error));
		});
	}

	handleSubmitFailure(error: unknown) {
		console.log(error);
		this.state.working = false;
		const message =
			error instanceof Error
				? error.message
				: 'We could not reach the server. Please check your connection and try again.';
		this.showErrorMessage(message);
		window.scrollTo({ top: window.scrollY + 300, behavior: 'smooth' });
		this.render();
	}

	async formResponse(res: Response) {
		this.state.working = false;
		let data: ArmorResponse;
		try {
			const text = await res.text();
			data = text ? JSON.parse(text) : {};
		} catch {
			this.handleSubmitFailure(
				new Error('We received an unexpected response. Please try again or contact support.')
			);
			return;
		}

		window.scrollTo({ top: window.scrollY + 300, behavior: 'smooth' });

		if (data.message === 'success') {
			this.showSuccessMessage(data);
			this.state.success = true;
		} else {
			this.showErrorMessage(this.errorMessageFromResponse(data));
		}
		this.render();
	}

	errorMessageFromResponse(data: ArmorResponse) {
		if (data.detail) return data.detail;
		const defaults: Record<string, string> = {
			serial_duplicate:
				'This serial number is already registered. Please verify the serial on your unit or contact support.',
			not_found:
				'This registration link is invalid or has expired. Please use the link from your order email or contact support.',
			invalid_state:
				'This registration can no longer be submitted online. Please contact support for help.',
			bad_request: 'Please check the form and try again.',
			unauthorized:
				'This submission could not be authorized. Please refresh the page and try again.',
			error: 'Something went wrong. Please try again or contact support.',
			'Recaptcha fail': 'Verification failed. Please refresh the page and try again.',
		};
		return (data.message && defaults[data.message]) || 'Request could not be processed.';
	}

	successBody(data: ArmorResponse) {
		if (data.outcome === 'already_submitted') {
			return `
      <p>We already have your registration for this unit.</p>
      <p>If you need to change something, <a href="mailto:support@freeflysystems.com">contact support</a>.</p>`;
		}
		if (data.path === 'awaiting_agent') {
			return `
      <p>We received your armor registration.</p>
      <p>Our team will review the details you provided (including your serial number) and follow up about your certificate.</p>
      ${data.waluWarning ? `<p><em>${data.waluWarning}</em></p>` : ''}`;
		}
		return `
      <p>We received your armor registration.</p>
      <p>Once your order is fulfilled, we'll reach out to deliver your armor registration certificate.</p>
      ${data.waluWarning ? `<p><em>${data.waluWarning}</em></p>` : ''}`;
	}

	formKeyUp() {
		this.validate();
		this.render();
	}

	globalInput() {
		this.validate();
		this.render();
	}

	render() {
		this.renderErrors();
		this.renderFooter();
	}

	renderErrors() {
		if (this.state.emailIsValid === false) {
			this.DOM.email.classList.add('js-error');
		} else {
			this.DOM.email.classList.remove('js-error');
		}
	}

	renderFooter() {
		if (this.state.valid && !this.state.working && !this.state.success) {
			this.DOM.submitButton.disabled = false;
		} else {
			this.DOM.submitButton.disabled = true;
		}
		if (this.state.working) {
			this.DOM.spinner.classList.remove('js-hide');
		} else {
			this.DOM.spinner.classList.add('js-hide');
		}
	}

	validate() {
		let valid = true;
		for (const input of this.DOM.allInputs) {
			if (!input.required || input.value) continue;
			valid = false;
		}

		if (this.DOM.email.value) {
			if (!this.validateEmail(this.DOM.email.value)) {
				valid = false;
				this.state.emailIsValid = false;
			} else {
				this.state.emailIsValid = true;
			}
		}

		this.state.valid = valid;
		return valid;
	}

	validateEmail(email: string) {
		if (email.length > 100) return false;
		const split = email.split('@');
		if (split.length !== 2 || split[0] === '' || split[1] === '') {
			return false;
		}
		const domainSplit = split[1].split('.');
		const suffix = domainSplit[domainSplit.length - 1];
		if (domainSplit.length === 1 || suffix.length < 2) {
			return false;
		}
		return true;
	}

	showSuccessMessage(data: ArmorResponse = {}) {
		const section = document.createElement('div');
		section.classList.add('section', 'messageSection', 'success');
		const html = `
<div class="sectionHeader">
  <h2 class="sectionHl">Success!</p>
</div>
<div class="sectionBody">
  <div class="formRow">
    <div class="formCol">
      ${this.successBody(data)}
    </div>
  </div>
</div>
`;
		section.innerHTML = html;
		this.DOM.form.appendChild(section);
	}

	showErrorMessage(message: string) {
		const section = document.createElement('div');
		section.classList.add('section', 'messageSection', 'error');
		const safeMessage = typeof message === 'string' ? message : 'Request could not be processed.';
		const html = `
<div class="sectionHeader">
  <h2 class="sectionHl">Error</p>
</div>
<div class="sectionBody">
  <div class="formRow">
    <div class="formCol">
      <p>Uh oh. There was an issue with the form submission:</p>
      <p>${safeMessage}</p>
      <p><a href="mailto:support@freeflysystems.com">Email support</a> for help with your registration.</p>
    </div>
  </div>
</div>
`;
		section.innerHTML = html;
		this.DOM.form.appendChild(section);
	}

	disableForm() {
		this.state.disabled = true;
		this.DOM.submitButton.parentElement?.removeChild(this.DOM.submitButton);

		const section = document.createElement('div');
		section.classList.add('section', 'messageSection', 'error');
		const html = `
<div class="sectionHeader">
  <h2 class="sectionHl">Error</p>
</div>
<div class="sectionBody">
  <div class="formRow">
    <div class="formCol">
      <p>Your link is invalid.</p>
      <p><a href="mailto:support@freeflysystems.com">Email support</a> for help with your registration.</p>
    </div>
  </div>
</div>
`;
		section.innerHTML = html;
		this.DOM.form.prepend(section);
	}
}
new FFArmor();
